import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Heart, ChevronDown, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

const TOP_LINKS = [
  { to: "/", label: "Home" },
  { label: "About", parent: "about" },
  { label: "Services", parent: "services", indexTo: "/services" },
  { label: "Programs", parent: "programs" },
  { label: "New to Islam", parent: "new-to-islam" },
  { to: "/events", label: "Events" },
  { to: "/prayer-times", label: "Prayer Times" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [pages, setPages] = useState([]);
  const [services, setServices] = useState([]);
  const [expanded, setExpanded] = useState(null); // which mobile section is expanded

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    api.get("/pages").then((r) => setPages(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const subPages = (parent) => {
    if (parent === "services") {
      const list = services
        .filter((s) => s.is_published !== false)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((s) => ({ slug: s.slug, title: s.title, _serviceRoute: true }));
      // Inject Community Resources as a sub-item right BEFORE Community Outreach
      const outreachIdx = list.findIndex((s) => s.slug === "outreach");
      const cr = { slug: "community-resources", title: "Community Resources", _directTo: "/community-resources" };
      if (outreachIdx >= 0) list.splice(outreachIdx, 0, cr);
      else list.push(cr);
      return list;
    }
    return pages.filter((p) => p.parent === parent).sort((a, b) => a.order - b.order);
  };
  const closeMenu = () => { setOpen(false); setExpanded(null); };

  return (
    <>
      <header
        data-testid="site-header"
        style={{ top: "var(--fic-banner-h, 0px)" }}
        className={`fixed left-0 right-0 z-40 transition-all duration-300 ${
          scrolled ? "bg-cream/90 backdrop-blur-xl border-b border-sand shadow-sm" : "bg-cream/55 backdrop-blur-md"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group min-w-0">
            <img src="/fic-logo.png" alt="Flint Islamic Center" className="h-10 md:h-11 w-auto" />
            <div className="hidden sm:flex flex-col leading-tight min-w-0">
              <span className="font-serif text-base md:text-lg text-navy tracking-tight truncate">Flint Islamic Center</span>
              <span className="text-[9px] uppercase tracking-[0.22em] text-gold">Unity · Compassion · Community</span>
            </div>
          </Link>

          <nav className="hidden xl:flex items-center gap-7">
            {TOP_LINKS.map((l) => {
              if (l.to) {
                return (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `relative text-sm font-medium tracking-wide transition-colors py-2 ${isActive ? "text-navy" : "text-ink/70 hover:text-navy"} group`
                    }
                  >
                    {({ isActive }) => (
                      <>{l.label}<span className={`absolute -bottom-0.5 left-0 right-0 h-[2px] bg-gold rounded-full transition-all duration-300 ${isActive ? "scale-x-100 opacity-100" : "scale-x-0 opacity-0 group-hover:scale-x-50 group-hover:opacity-50"}`} /></>
                    )}
                  </NavLink>
                );
              }
              const subs = subPages(l.parent);
              const headerLink = l.indexTo; // if present, the section's own page (e.g. /services)
              return (
                <div key={l.label} className="relative group" data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {headerLink ? (
                    <NavLink
                      to={headerLink}
                      className={({ isActive }) =>
                        `flex items-center gap-1 text-sm font-medium transition-colors py-2 ${isActive ? "text-navy" : "text-ink/70 hover:text-navy"}`
                      }
                    >
                      {l.label} <ChevronDown size={12} className="mt-0.5 group-hover:rotate-180 transition-transform" />
                    </NavLink>
                  ) : (
                    <button className="flex items-center gap-1 text-sm font-medium text-ink/70 hover:text-navy transition-colors py-2">
                      {l.label} <ChevronDown size={12} className="mt-0.5 group-hover:rotate-180 transition-transform" />
                    </button>
                  )}
                  {subs.length > 0 && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                      <div className="rounded-2xl bg-white border border-sand shadow-2xl shadow-navy/10 py-2 min-w-[260px]">
                        {headerLink && (
                          <Link to={headerLink} className="block px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-gold hover:bg-sand/50" data-testid={`subnav-${l.parent}-index`}>
                            All {l.label}
                          </Link>
                        )}
                        {subs.map((p) => (
                          <Link
                            key={p.slug}
                            to={p._directTo || (p._serviceRoute ? `/services/${p.slug}` : (p.slug === "gallery" ? "/gallery" : `/${p.slug}`))}
                            className="block px-5 py-2.5 text-sm text-ink/75 hover:bg-sand/50 hover:text-navy hover:pl-6 transition-all"
                            data-testid={`subnav-${p.slug}`}
                          >
                            {p.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/donate" data-testid="nav-donate-btn" className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full bg-navy text-cream px-3.5 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-medium hover:bg-navy-900 transition-all hover:shadow-lg hover:-translate-y-0.5">
              <Heart size={14} className="fill-gold stroke-gold" />
              <span>Donate</span>
            </Link>
            <button data-testid="nav-mobile-toggle" className="xl:hidden p-2 text-ink" onClick={() => setOpen((v) => !v)} aria-label="Toggle menu">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      {/* MOBILE DRAWER */}
      <div
        className={`xl:hidden fixed inset-0 z-50 transition-opacity duration-300 ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        data-testid="mobile-menu"
        aria-hidden={!open}
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-navy/60 backdrop-blur-sm" onClick={closeMenu} />

        {/* Drawer panel — 80% width, slide from right */}
        <aside
          className={`absolute top-0 right-0 h-full w-[82%] max-w-[380px] bg-cream shadow-2xl shadow-navy/40 transform transition-transform duration-300 ease-out flex flex-col ${
            open ? "translate-x-0" : "translate-x-full"
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-5 border-b border-sand">
            <Link to="/" onClick={closeMenu} className="flex items-center gap-2.5">
              <img src="/fic-logo.png" alt="FIC" className="h-9 w-auto" />
              <div className="font-serif text-navy text-base leading-tight">FIC</div>
            </Link>
            <button onClick={closeMenu} className="p-2 -m-2 text-ink/70 hover:text-navy" aria-label="Close menu">
              <X size={22} />
            </button>
          </div>

          {/* Links */}
          <nav className="flex-1 overflow-y-auto px-3 py-3">
            {TOP_LINKS.map((l) => {
              if (l.to) {
                return (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.to === "/"}
                    onClick={closeMenu}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `flex items-center justify-between px-4 py-3.5 my-0.5 rounded-xl text-base font-medium transition-all ${
                        isActive
                          ? "bg-navy text-cream shadow-md shadow-navy/20"
                          : "text-ink hover:bg-gold/15 hover:text-navy hover:translate-x-0.5"
                      }`
                    }
                  >
                    <span>{l.label}</span>
                  </NavLink>
                );
              }

              const subs = subPages(l.parent);
              const isExpanded = expanded === l.parent;
              return (
                <div key={l.label} className="my-0.5">
                  <button
                    onClick={() => setExpanded(isExpanded ? null : l.parent)}
                    data-testid={`mobile-section-${l.parent}`}
                    className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl text-base font-semibold transition-all ${
                      isExpanded ? "bg-gold/15 text-navy" : "text-ink hover:bg-gold/10 hover:text-navy"
                    }`}
                  >
                    <span>{l.label}</span>
                    <ChevronRight
                      size={16}
                      className={`text-gold transition-transform duration-300 ${isExpanded ? "rotate-90" : ""}`}
                    />
                  </button>

                  {/* Collapsible content — use a large upper-bound max-height so the
                      items are always visible regardless of zoom / font size. */}
                  <div
                    className="overflow-hidden transition-[max-height,opacity] duration-300 ease-out"
                    style={{ maxHeight: isExpanded ? "1000px" : "0px", opacity: isExpanded ? 1 : 0 }}
                  >
                    <div className="pl-3 pt-1 pb-2 border-l-2 border-gold/30 ml-4 space-y-0.5">
                      {l.indexTo && (
                        <NavLink
                          to={l.indexTo}
                          end
                          onClick={closeMenu}
                          data-testid={`mobile-subnav-${l.parent}-index`}
                          className="block px-4 py-2 rounded-lg text-[10px] uppercase tracking-[0.2em] font-semibold text-gold hover:bg-sand/40"
                        >
                          All {l.label}
                        </NavLink>
                      )}
                      {subs.map((p) => (
                        <NavLink
                          key={p.slug}
                          to={p._directTo || (p._serviceRoute ? `/services/${p.slug}` : (p.slug === "gallery" ? "/gallery" : `/${p.slug}`))}
                          onClick={closeMenu}
                          data-testid={`mobile-subnav-${p.slug}`}
                          className={({ isActive }) =>
                            `block px-4 py-2.5 rounded-lg text-sm transition-all ${
                              isActive ? "bg-navy text-cream" : "text-ink/75 hover:bg-sand/60 hover:text-navy hover:translate-x-1"
                            }`
                          }
                        >
                          {p.title}
                        </NavLink>
                      ))}
                      {subs.length === 0 && (
                        <div className="px-4 py-2.5 text-xs text-ink/45 italic">No pages yet — add them in admin.</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </nav>

          {/* Footer CTA */}
          <div className="px-5 py-4 border-t border-sand bg-white">
            <Link
              to="/donate"
              onClick={closeMenu}
              data-testid="mobile-donate-btn"
              className="w-full inline-flex justify-center items-center gap-2 rounded-full bg-navy text-cream px-5 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-navy-900"
            >
              <Heart size={14} className="fill-gold stroke-gold" /> Donate Now
            </Link>
          </div>
        </aside>
      </div>
    </>
  );
}
